import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDashboardDataRequest } from "services/modules/dashboard/index";
import { Box, Grid, Typography, Button, useTheme } from "@mui/material";
import StatsCard from "./components/StatsCard";
import FinancialChart from "./components/FinancialChart";
import OrdersStatusChart from "./components/OrdersStatusChart";
import PaidIcon from "@mui/icons-material/Paid";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PeopleIcon from "@mui/icons-material/People";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CrisisAlertIcon from "@mui/icons-material/CrisisAlert";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SaudiRyal from "assets/saudiRyal";
import BasicDatePicker from "components/Atoms/Input/DatePicker/DatePicker";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { dashboardData } = useSelector((state) => state.dashboard);
  const [selectedPeriod, setSelectedPeriod] = useState("this_month");
  const [customDateRange, setCustomDateRange] = useState({
    start_date: dayjs().startOf("month"),
    end_date: dayjs(),
  });

   const isStore = localStorage?.getItem("user_type") === "Store";

  useEffect(() => {
    const params = {
      date_range_type: selectedPeriod,
    };
    if (selectedPeriod === "custom") {
      params.start_date = customDateRange.start_date
        ? customDateRange.start_date.format("YYYY-MM-DD")
        : null;
      params.end_date = customDateRange.end_date
        ? customDateRange.end_date.format("YYYY-MM-DD")
        : null;
    }

    dispatch(getDashboardDataRequest({ params }));
  }, [dispatch, selectedPeriod, customDateRange]);

  const periods = [
    {
      id: "custom",
      label: t("dashboard.custom_range"),
      icon: <CalendarTodayIcon fontSize="small" />,
    },
    { id: "past_year", label: t("dashboard.last_year") },
    { id: "this_year", label: t("dashboard.this_year") },
    { id: "this_month", label: t("dashboard.this_month") },
  ];

  const statictis = [
    {
      title: t("dashboard.total_submitted_orders"),
      value: dashboardData?.performance?.total_submitted_orders || 0,
      icon: <CrisisAlertIcon />,
      color: "#4CAF50",
    },
    {
      title: t("dashboard.total_active_users"),
      value: dashboardData?.performance?.total_active_users || 0,
      icon: <PeopleIcon />,
      color: "#F44336",
    },
    {
      title: t("dashboard.total_products"),
      value: dashboardData?.performance?.total_products || 0,
      icon: <Inventory2Icon />,
      color: "#00BCD4",
    },
    {
      title: t("dashboard.total_collected_amount"),
      value: (
        <span style={{ display: "flex", alignItems: "center" }}>
          {dashboardData?.performance?.total_collected_amount || 0}{" "}
          <SaudiRyal />
        </span>
      ),
      icon: <PaidIcon />,
      color: "#6C4192",
    },
  ];

  const reportStatistics = [
    {
      title: t("dashboard.sales_reports_commissions"),
      value: (
        <span style={{ display: "flex", alignItems: "center" }}>
          {dashboardData?.reports?.total_cadeau_commission || 0} <SaudiRyal />
        </span>
      ),
      icon: <ReceiptIcon />,
      color: "#FF9800",
      onClick: () => navigate("/dashboard/sales-report"),
    },
    {
      title: t("dashboard.sales_reports_quantities"),
      value: dashboardData?.reports?.total_sold_quantities || 0,
      icon: <ShoppingCartIcon />,
      color: "#2196F3",
      onClick: () => navigate("/dashboard/sold-quantities-report"),
    },
    {
      title: t("dashboard.reports_best_selling_products"),
      value: (
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#9C27B0",
            "&:hover": { backgroundColor: "#7B1FA2" },
            borderRadius: "8px",
            textTransform: "none",
            mt: 1,
            mb: 1,
            paddingX: 5,
            width: "100%",
          }}
          onClick={(e) => {
            e.stopPropagation();
            navigate("/dashboard/best-selling-products");
          }}
          size="small"
        >
          {t("dashboard.view_products")}
        </Button>
      ),
      icon: <TrendingUpIcon />,
      color: "#9C27B0",
      onClick: () => navigate("/dashboard/best-selling-products"),
    },
    {
      title: t("dashboard.vat_report"),
      value: (
        <span style={{ display: "flex", alignItems: "center" }}>
          {dashboardData?.reports?.total_vat || 0} <SaudiRyal />
        </span>
      ),
      icon: <ReceiptIcon />,
      color: "#E91E63",
      onClick: () => navigate("/dashboard/vat-report"),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {t("dashboard.title")}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {periods.map((period) => (
            <Button
              key={period.id}
              variant={selectedPeriod === period.id ? "contained" : "outlined"}
              color="secondary"
              onClick={() => setSelectedPeriod(period.id)}
              startIcon={period.icon}
              sx={{
                borderRadius: "12px",
                borderColor: theme.palette.divider,
                height: 44,
                padding: "12px 16px",
                backgroundColor:
                  selectedPeriod === period.id ? "#F3E5F5" : "transparent", // Light purple bg
                color:
                  selectedPeriod === period.id
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                border:
                  selectedPeriod === period.id
                    ? `1px solid ${theme.palette.primary.main}`
                    : `1px solid ${theme.palette.divider}`,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor:
                    selectedPeriod === period.id ? "#E1BEE7" : "#f5f5f5",
                  boxShadow: "none",
                  border:
                    selectedPeriod === period.id
                      ? `1px solid ${theme.palette.primary.main}`
                      : `1px solid ${theme.palette.divider}`,
                },
              }}
            >
              {period.label}
            </Button>
          ))}
        </Box>
      </Box>

      {selectedPeriod === "custom" && (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3,
            justifyContent: { xs: "center", sm: "flex-end" },
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <BasicDatePicker
            label={t("dashboard.start_date")}
            value={customDateRange.start_date}
            onChange={(newValue) => {
              setCustomDateRange((prev) => ({ ...prev, start_date: newValue }));
            }}
            maxDate={customDateRange.end_date}
            filter={false}
          />
          <BasicDatePicker
            label={t("dashboard.end_date")}
            value={customDateRange.end_date}
            onChange={(newValue) => {
              setCustomDateRange((prev) => ({ ...prev, end_date: newValue }));
            }}
            minDate={customDateRange.start_date}
            filter={false}
          />
        </Box>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        {statictis.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={`stat-${index}`}>
            <StatsCard
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              color={stat.color}
            />
          </Grid>
        ))}
      </Grid>

      {/* Reports Cards */}
      {isStore ? null :
      <Grid container spacing={3} mb={5}>
        {reportStatistics.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={`report-${index}`}>
            <StatsCard
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              color={stat.color}
              onClick={stat.onClick}
            />
          </Grid>
        ))}
      </Grid>}

      {/* Charts Section */}
      <Typography variant="h4" mb={3} sx={{ fontWeight: 700 }}>
        {t("dashboard.orders_by_status")}
      </Typography>
      <Grid container spacing={3}>
        {/* Area Chart (Visual Left) */}
        <Grid item xs={12} md={5} paddingX={1}>
          <FinancialChart
            data={dashboardData?.order_performance?.monthly_breakdown}
          />
        </Grid>
        {/* Bar Chart (Visual Right) */}
        <Grid item xs={12} md={7} paddingX={1}>
          <OrdersStatusChart
            data={dashboardData?.order_performance?.status_breakdown}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
